import React, { useState, useEffect } from 'react';
import { Modal, Input, List, Avatar, Typography, Space, theme } from 'antd';
import { UserOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useDebounce } from '../hooks/useDebounce';

const { Text } = Typography;

interface User {
  id: string;
  email: string;
  username: string;
  full_name: string;
}

interface ImpersonationModalProps {
  visible: boolean;
  onCancel: () => void;
}

const ImpersonationModal: React.FC<ImpersonationModalProps> = ({ visible, onCancel }) => {
  const { t } = useTranslation('admin');
  const [searchQuery, setSearchQuery] = useState('');
  // Запрос идёт по ОТЛОЖЕННОМУ значению: без этого каждое нажатие клавиши
  // переписывало queryKey и порождало новый запрос к /users/search (#396).
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { impersonate } = useAuthStore();
  const { token: themeToken } = theme.useToken();

  const { data: users, isLoading } = useQuery({
    queryKey: ['users', 'search', debouncedSearchQuery],
    queryFn: async ({ signal }) => {
      const response = await api.get('/users/search', {
        params: { q: debouncedSearchQuery },
        signal,
      });
      return response.data.data as User[];
    },
    enabled: debouncedSearchQuery.length >= 2,
    staleTime: 1000,
  });

  useEffect(() => {
    if (!visible) {
      setSearchQuery('');
      setSelectedUser(null);
    }
  }, [visible]);

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
  };

  const handleConfirm = async () => {
    if (!selectedUser) return;

    try {
      const response = await api.post('/auth/impersonate', {
        user_id: selectedUser.id,
      });

      // Refresh token rotated into the HttpOnly cookie by the backend; only the
      // access token is in the body.
      const { token, user: impersonatedUser, original_user } = response.data.data;

      // Обновляем токен и состояние
      impersonate(token, impersonatedUser, original_user);

      toast.success(t('impersonationModal.toast.success', { name: impersonatedUser.full_name || impersonatedUser.email }));

      // Перезагружаем страницу для применения нового токена
      window.location.reload();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('impersonationModal.toast.error'));
    }
  };

  return (
    <Modal
      title={t('impersonationModal.title')}
      open={visible}
      onCancel={onCancel}
      onOk={handleConfirm}
      okText={t('impersonationModal.ok')}
      cancelText={t('impersonationModal.cancel')}
      okButtonProps={{ disabled: !selectedUser }}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Input
          placeholder={t('impersonationModal.searchPlaceholder')}
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          allowClear
        />

        {searchQuery.length < 2 && (
          <Text type="secondary">{t('impersonationModal.minTwoChars')}</Text>
        )}

        {searchQuery.length >= 2 && (
          <div role="listbox">
          <List
            loading={isLoading}
            dataSource={users || []}
            style={{ maxHeight: '400px', overflowY: 'auto' }}
            renderItem={(user) => (
              <List.Item
                role="option"
                aria-selected={selectedUser?.id === user.id}
                tabIndex={0}
                onClick={() => handleUserSelect(user)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleUserSelect(user);
                  }
                }}
                style={{
                  cursor: 'pointer',
                  backgroundColor: selectedUser?.id === user.id ? themeToken.colorPrimaryBg : 'transparent',
                  padding: '12px',
                  borderRadius: '4px',
                  border: selectedUser?.id === user.id ? `1px solid ${themeToken.colorPrimaryBorder}` : '1px solid transparent',
                }}
              >
                <List.Item.Meta
                  avatar={<Avatar icon={<UserOutlined />} />}
                  title={user.full_name || user.username || user.email}
                  description={user.email}
                />
              </List.Item>
            )}
            locale={{ emptyText: t('impersonationModal.usersNotFound') }}
          />
          </div>
        )}

        {selectedUser && (
          <div style={{
            padding: '12px',
            backgroundColor: themeToken.colorFillAlter,
            borderRadius: '4px',
            border: `1px solid ${themeToken.colorBorderSecondary}`
          }}>
            <Text strong>{t('impersonationModal.selectedUser')}</Text>
            <br />
            <Text>{selectedUser.full_name || selectedUser.username || selectedUser.email}</Text>
            <br />
            <Text type="secondary">{selectedUser.email}</Text>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default ImpersonationModal;
